import {
  CalculatePriceInterface,
  DigitalCheckout as Checkout,
  StoreImageEnum,
} from '@systemeio/digital-store-shared'
import { cents, CurrencyEnum, FormInput, PaymentMethodEnum } from '@systemeio/ui'
import { toPercent } from '@systemeio/util'
import { DigitalProductChangeInterface } from 'modules/digital-product/types/digital-product-interface'
import StoreFields from 'modules/stores/store/components/contact-fields'
import ItemSteps from 'modules/stores/store/components/item-steps'
import StoreImageSelector from 'modules/stores/store/components/store-image-selector'
import { StoreTextEditor } from 'modules/stores/store/components/store-text-editor'
import PaymentMethodsEditModal from 'modules/stores/store/content/product/components/payment-methods-edit-modal'
import { PricePlanItem } from 'modules/stores/store/content/product/digital-product/components/price-plan-item'
import { digitalProductInitFields } from 'modules/stores/store/content/product/digital-product/constants/mandatory-fields'
import {
  ProductDownloadItemEditType,
  ProductDownloadItemErrorsType,
  ProductECourseItemEditType,
  ProductECourseItemErrorsType,
} from 'modules/stores/store/content/product/types/product-interface'
import { useStorePreviewContext } from 'modules/stores/store/context/store-preview-context'
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twJoin } from 'tailwind-merge'

interface Props<
  StateType extends ProductDownloadItemEditType | ProductECourseItemEditType,
  ErrorsType extends ProductDownloadItemErrorsType | ProductECourseItemErrorsType,
> {
  tempState: StateType
  setTempState: Dispatch<SetStateAction<StateType>>
  tempProduct: DigitalProductChangeInterface
  setTempProduct: Dispatch<SetStateAction<DigitalProductChangeInterface>>
  errors: ErrorsType
  pricePlanErrors: string[]
  currency?: CurrencyEnum
  paymentMethods?: PaymentMethodEnum[]
  imageType: StoreImageEnum
}

const CheckoutTab = <
  StateType extends ProductDownloadItemEditType | ProductECourseItemEditType,
  ErrorsType extends ProductDownloadItemErrorsType | ProductECourseItemErrorsType,
>({
  tempState,
  setTempState,
  errors,
  setTempProduct,
  tempProduct,
  currency,
  paymentMethods,
  pricePlanErrors,
  imageType,
}: Props<StateType, ErrorsType>) => {
  const { t } = useLocoTranslation()

  const { setPreview, setSingleItemMode } = useStorePreviewContext()

  const calculatePrice: CalculatePriceInterface | undefined = useMemo(() => {
    const firstPricePlan = tempProduct.pricePlans?.[0]

    if (!firstPricePlan) return

    return {
      originalAmount: firstPricePlan.amount,
      grossAmount: firstPricePlan.amount,
      pricePlan: firstPricePlan.id,
      quantity: 1,
      netAmount: firstPricePlan.amount,
      discountedNetAmount: firstPricePlan.amount,
      tax: {
        amount: cents(0),
        rate: toPercent(0),
      },
      discount: {
        amount: cents(0),
        percent: toPercent(0),
      },
      shippingFee: {
        amount: cents(0),
        tax: {
          amount: cents(0),
          rate: toPercent(0),
        },
      },
      currencyCode: firstPricePlan.currency,
    }
  }, [tempProduct.pricePlans])

  useEffect(() => {
    setPreview([
      <Checkout
        key={'Checkout page preview'}
        calculatePrice={calculatePrice}
        checkoutPage={tempState.checkoutPage}
        paymentMethods={paymentMethods}
        currency={currency}
        pricePlans={tempProduct.pricePlans}
        thankYouPage={tempState.thankYouPage}
        productType={tempState.productType}
        displayAffiliateLink={false}
        skipDisabledButton={true}
      />,
    ])
    setSingleItemMode(false)
  }, [
    calculatePrice,
    currency,
    paymentMethods,
    setPreview,
    setSingleItemMode,
    tempProduct.pricePlans,
    tempState,
  ])

  return (
    <ItemSteps
      steps={[
        {
          key: 'image',
          title: t('dashboard.store.image.select_image'),
          element: (
            <StoreImageSelector
              dataFile={tempState.checkoutPage.cover}
              imageType={imageType}
              label={t('dashboard.store.product.checkout_image_label')}
              onChange={dataFile =>
                setTempState(prev => ({
                  ...prev,
                  checkoutPage: {
                    ...prev.checkoutPage,
                    cover: dataFile,
                  },
                }))
              }
            />
          ),
        },
        {
          key: 'text',
          title: t('dashboard.store.item.add_text'),
          element: (
            <div className={'flex flex-col gap-5'}>
              <div className={'grid grid-cols-1 gap-5 sm:grid-cols-2'}>
                <FormInput
                  required
                  value={tempState.checkoutPage.title}
                  label={t('dashboard.store.item.title')}
                  onChange={({ target: { value } }) =>
                    setTempState(prev => ({
                      ...prev,
                      checkoutPage: { ...prev.checkoutPage, title: value },
                    }))
                  }
                  error={errors['checkoutPage.title']}
                />
                <FormInput
                  required
                  value={tempState.checkoutPage.buttonTitle}
                  label={t('dashboard.store.item.button')}
                  onChange={({ target: { value } }) =>
                    setTempState(prev => ({
                      ...prev,
                      checkoutPage: { ...prev.checkoutPage, buttonTitle: value },
                    }))
                  }
                  error={errors['checkoutPage.buttonTitle']}
                />
              </div>
              <StoreTextEditor
                label={t('dashboard.store.item.description')}
                required
                content={tempState.checkoutPage.description}
                onChange={value =>
                  setTempState(prev => ({
                    ...prev,
                    checkoutPage: {
                      ...prev.checkoutPage,
                      description: value,
                    },
                  }))
                }
                className={twJoin(errors['checkoutPage.description'] && 'border-danger')}
                error={errors['checkoutPage.description']}
              />
            </div>
          ),
        },
        {
          key: 'fields',
          title: t('dashboard.store.item.collect_info'),
          element: (
            <StoreFields
              fields={tempState.checkoutPage.fields}
              onChange={fields =>
                setTempState(prev => ({ ...prev, checkoutPage: { ...prev.checkoutPage, fields } }))
              }
              initFields={digitalProductInitFields}
              error={errors['checkoutPage.fields']}
            />
          ),
        },
        {
          key: 'price plans',
          title: t('dashboard.funnel.configuration.offer.price_plans'),
          element: (
            <PricePlanItem
              errors={[...pricePlanErrors, errors['digitalProduct.pricePlans']].filter(Boolean)}
              currency={currency}
              pricePlans={tempProduct.pricePlans}
              onChange={pricePlans => {
                setTempProduct(prev => ({ ...prev, pricePlans }))
              }}
              digitalProductId={tempProduct.id}
            />
          ),
        },
        {
          key: 'payment methods',
          title: t('dashboard.store.product.checkout.payment_methods'),
          element: (
            <PaymentMethodsEditModal
              errors={{ paymentMethods: errors['digitalStore.paymentMethods'] }}
            />
          ),
        },
      ]}
    />
  )
}

export default CheckoutTab
